import {
  type ComponentType,
  createContext,
  type PropsWithChildren,
  type ReactNode,
  Suspense,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import * as Dialog from '@radix-ui/react-dialog';
import { ArrowLeft } from 'lucide-react';

import { Button } from '@/components/shadcn/button';

export type ModalProps<T = object> = T & {
  onClose: () => void;
};

type ModalComponent<P> = ComponentType<P>;

type ModalComponentProps<P> = Omit<P, 'onClose'>;

type ModalOptions = {
  title?: ReactNode;
  size?: 'normal' | 'full';
  closeOnOverlayClick?: boolean;
  closeOnEscapeKey?: boolean;
  onDismiss?: () => void;
};

type Modal<P> = {
  component: ModalComponent<P>;
  props?: ModalComponentProps<P>;
  options?: ModalOptions;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ModalsState = Modal<any>[];

type Props = {
  open?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
};

type ShowFn = <P extends Props>(
  component: ModalComponent<P>,
  props?: ModalComponentProps<P>,
  options?: ModalOptions,
) => void;

type ModalContextState = {
  modals: ModalsState;
  showModal: ShowFn;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  closeModal: (modal: Modal<any>) => void;
  closeFullscreenModals: () => void;
};

const ModalContext = createContext<ModalContextState | undefined>(undefined);

type ModalContainerProps = {
  handleOpenChange: (open: boolean) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  modal: Modal<any>;
  handleClose: (index?: number) => void;
};

export const ModalContainer = ({
  handleOpenChange,
  modal,
  handleClose,
}: ModalContainerProps) => {
  const Component = modal.component;

  if (modal.options?.size === 'full') {
    return (
      <Dialog.Root open={true} onOpenChange={handleOpenChange} modal={false}>
        <div className="bg-background fixed top-0 right-0 bottom-0 left-0 z-50 flex w-full">
          <Dialog.Content
            aria-describedby={undefined}
            className="w-full overflow-hidden focus:outline-hidden"
          >
            <Dialog.Title className="hidden" />
            <div className="h-full overflow-y-auto">
              {modal.options?.title && (
                <div className="flex items-center py-2">
                  <Button variant="link" onClick={() => handleClose()}>
                    <ArrowLeft />
                  </Button>

                  {modal.options?.title}
                </div>
              )}

              <Component {...modal.props} onClose={handleClose} />
            </div>
          </Dialog.Content>
        </div>
      </Dialog.Root>
    );
  }

  return (
    <Dialog.Root open={true} onOpenChange={handleOpenChange} modal={false}>
      <div className="fixed top-0 right-0 bottom-0 left-0 z-50 flex w-full items-center justify-center bg-black/50 p-4">
        <Dialog.Content
          aria-describedby={undefined}
          className="bg-background max-h-full max-w-full overflow-y-auto rounded-lg focus:outline-hidden"
        >
          <Dialog.Title className="hidden" />

          <Component {...modal.props} onClose={handleClose} />
        </Dialog.Content>
      </div>
      <div className="fixed top-0 right-0 bottom-0 left-0" />
    </Dialog.Root>
  );
};

export const ModalsOutlet = () => {
  const { modals, closeModal } = useModalContext();

  const handleOpenChange = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (modal: Modal<any>) => (open: boolean) => {
      if (!open) closeModal(modal);
    },
    [closeModal],
  );

  return (
    <Suspense fallback={null}>
      {modals.map((modal, index) => (
        <ModalContainer
          key={index}
          modal={modal}
          handleOpenChange={handleOpenChange(modal)}
          handleClose={() => closeModal(modal)}
        />
      ))}
    </Suspense>
  );
};

export const ModalsProvider = ({ children }: PropsWithChildren) => {
  const [modals, setModals] = useState<ModalsState>([]);

  const showModal = useCallback<ShowFn>((component, props, options) => {
    setModals(() => [
      {
        component,
        props,
        options,
      },
    ]);
  }, []);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const closeModal = useCallback((modal: Modal<any>) => {
    modal.options?.onDismiss?.();
    setModals((openModals) =>
      openModals.filter((openModal) => openModal !== modal),
    );
  }, []);

  const closeFullscreenModals = useCallback(
    () =>
      setModals((modals) =>
        modals.filter((modal) => modal.options?.size !== 'full'),
      ),
    [],
  );

  const value = useMemo(
    () => ({ modals, showModal, closeModal, closeFullscreenModals }),
    [modals, showModal, closeModal, closeFullscreenModals],
  );

  return (
    <ModalContext.Provider value={value}>{children}</ModalContext.Provider>
  );
};

const useModalContext = () => {
  const context = useContext(ModalContext);

  if (!context) {
    throw new Error('useModal must be used within a ModalsProvider');
  }

  return context;
};

export const useModal = () => {
  const { showModal, closeFullscreenModals } = useModalContext();

  return { showModal, closeFullscreenModals };
};

export default ModalsProvider;
